/**
 * Sugar Town Commerce — Native search, relevance ranking & smart filtering.
 *
 * Pure, framework-agnostic functions over the editorial metadata already stored
 * on `commerce_products`. This powers the native storefront search box and the
 * faceted filter sidebar. Shopify is never referenced here.
 *
 * Search is performed client-side against a cached published-catalog snapshot
 * so it stays instant on mobile networks and ranks results across many fields
 * (name, ingredients, wellness goals, concerns, brand, collection, ritual
 * stage, sensory profile, seasonality, etc.).
 */
import { supabase } from "@/integrations/supabase/client";
import type { CommerceProduct } from "./types";

// ---------- Filter facet definitions ----------

export interface CommerceFilters {
  brandSlugs: string[];
  productTypes: string[];
  wellnessGoals: string[];
  concerns: string[];
  ingredients: string[];
  fragranceFamilies: string[];
  seasonality: string[];
  priceMin?: number;
  priceMax?: number;
  vegan?: boolean;
  crueltyFree?: boolean;
  bundle?: boolean;
  giftSet?: boolean;
  seasonal?: boolean;
  inStock?: boolean;
}

export const EMPTY_FILTERS: CommerceFilters = {
  brandSlugs: [],
  productTypes: [],
  wellnessGoals: [],
  concerns: [],
  ingredients: [],
  fragranceFamilies: [],
  seasonality: [],
};

export function hasActiveFilters(f: CommerceFilters): boolean {
  return (
    f.brandSlugs.length > 0 ||
    f.productTypes.length > 0 ||
    f.wellnessGoals.length > 0 ||
    f.concerns.length > 0 ||
    f.ingredients.length > 0 ||
    f.fragranceFamilies.length > 0 ||
    f.seasonality.length > 0 ||
    f.priceMin != null ||
    f.priceMax != null ||
    Boolean(f.vegan) ||
    Boolean(f.crueltyFree) ||
    Boolean(f.bundle) ||
    Boolean(f.giftSet) ||
    Boolean(f.seasonal) ||
    Boolean(f.inStock)
  );
}

import {
  aggregateVariantStatuses,
  classifyQuantity,
  type CommerceInventoryStatus,
} from "./inventory";

// ---------- Catalog snapshot (cached) ----------

/** A lightweight product joined with the facets needed for search & filtering. */
export interface SearchableProduct extends CommerceProduct {
  brand_slug: string | null;
  brand_name: string | null;
  in_stock: boolean;
  /** Normalized product-level availability status (see inventory.ts). */
  availability_status: CommerceInventoryStatus;
}

let snapshotCache: { at: number; data: SearchableProduct[] } | null = null;
const SNAPSHOT_TTL = 1000 * 60 * 5; // 5 minutes

export function invalidateCatalogSnapshot() {
  snapshotCache = null;
}

/** Load (and cache) the full published catalog with the facets we search over. */
export async function getCatalogSnapshot(force = false): Promise<SearchableProduct[]> {
  if (!force && snapshotCache && Date.now() - snapshotCache.at < SNAPSHOT_TTL) {
    return snapshotCache.data;
  }

  const [{ data: products, error }, { data: brands }, { data: variants }] = await Promise.all([
    supabase.from("commerce_products").select("*").eq("status", "published"),
    supabase.from("commerce_brands").select("id, slug, name"),
    supabase
      .from("commerce_product_variants")
      .select("product_id, inventory_quantity, inventory_policy, is_active"),
  ]);
  if (error) throw error;

  const brandById = new Map((brands ?? []).map((b) => [b.id, b]));
  const variantsByProduct = new Map<
    string,
    { qty: number | null; policy: string | null }[]
  >();
  for (const v of variants ?? []) {
    if (v.is_active === false) continue;
    const arr = variantsByProduct.get(v.product_id) ?? [];
    arr.push({
      qty: v.inventory_quantity ?? null,
      policy: (v as { inventory_policy?: string | null }).inventory_policy ?? null,
    });
    variantsByProduct.set(v.product_id, arr);
  }

  const data: SearchableProduct[] = (products ?? []).map((p) => {
    const brand = p.brand_id ? brandById.get(p.brand_id) : null;
    const vs = variantsByProduct.get(p.id) ?? [];
    // Normalized availability using the SAME aggregation rule as inventory.ts,
    // so the search snapshot never diverges from the inventory adapter.
    const status: CommerceInventoryStatus =
      vs.length === 0
        ? p.track_inventory
          ? "unknown"
          : "in_stock"
        : aggregateVariantStatuses(
            vs.map((v) =>
              classifyQuantity(v.qty, { trackInventory: !!p.track_inventory, policy: v.policy })
            )
          );
    return {
      ...p,
      brand_slug: brand?.slug ?? null,
      brand_name: brand?.name ?? null,
      availability_status: status,
      // "In stock" for the filter = anything other than a hard sold-out /
      // unavailable state.
      in_stock: status !== "out_of_stock" && status !== "unavailable",
    };
  });

  snapshotCache = { at: Date.now(), data };
  return data;
}

// ---------- Relevance search ----------

function tokenize(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1);
}

function arr(v: unknown): string[] {
  return Array.isArray(v) ? (v as string[]) : [];
}

/**
 * Build the weighted, searchable text fields for a product. Higher-weight
 * fields are matched first so the most meaningful hits rank highest.
 */
function fieldWeights(p: SearchableProduct): Array<{ text: string; weight: number }> {
  const sensory = p.sensory_profile && typeof p.sensory_profile === "object" && !Array.isArray(p.sensory_profile)
    ? Object.values(p.sensory_profile as Record<string, unknown>).filter((x): x is string => typeof x === "string")
    : [];
  return [
    { text: p.title ?? "", weight: 10 },
    { text: p.subtitle ?? "", weight: 6 },
    { text: p.brand_name ?? "", weight: 6 },
    { text: arr(p.ingredient_tags).join(" "), weight: 5 },
    { text: arr(p.wellness_goals).join(" "), weight: 5 },
    { text: arr(p.concerns).join(" "), weight: 5 },
    { text: p.ritual_stage ?? "", weight: 4 },
    { text: arr(p.tags).join(" "), weight: 3 },
    { text: arr(p.suitable_customer_types).join(" "), weight: 3 },
    { text: arr(p.lifestyle_use_cases).join(" "), weight: 3 },
    { text: arr(p.seasonality).join(" "), weight: 2 },
    { text: sensory.join(" "), weight: 2 },
    { text: p.ingredients ?? "", weight: 2 },
    { text: p.description ?? "", weight: 1 },
    { text: p.benefits ?? "", weight: 1 },
  ];
}

export interface SearchHit {
  product: SearchableProduct;
  score: number;
}

/** Rank a catalog by relevance to a free-text query. */
export function rankBySearch(catalog: SearchableProduct[], query: string): SearchHit[] {
  const tokens = tokenize(query);
  if (tokens.length === 0) return catalog.map((product) => ({ product, score: 0 }));

  const hits: SearchHit[] = [];
  for (const product of catalog) {
    const fields = fieldWeights(product);
    let score = 0;
    let matchedTokens = 0;

    for (const token of tokens) {
      let tokenMatched = false;
      for (const f of fields) {
        const hay = f.text.toLowerCase();
        if (!hay) continue;
        if (hay === token) {
          score += f.weight * 2;
          tokenMatched = true;
        } else if (hay.includes(token)) {
          // Word-boundary matches rank higher than substring matches.
          score += new RegExp(`\\b${token}`).test(hay) ? f.weight : f.weight * 0.5;
          tokenMatched = true;
        }
      }
      if (tokenMatched) matchedTokens += 1;
    }

    // Require at least one token to match; reward products that match all tokens.
    if (matchedTokens > 0) {
      score *= 1 + (matchedTokens - 1) * 0.5;
      if (matchedTokens === tokens.length) score *= 1.4;
      hits.push({ product, score });
    }
  }

  hits.sort((a, b) => b.score - a.score);
  return hits;
}

// ---------- Smart filtering ----------

const VEGAN_TOKENS = ["vegan", "100% vegan", "plant-based", "plant based"];
const CRUELTY_TOKENS = ["cruelty-free", "cruelty free"];

function lowerArr(v: unknown): string[] {
  return arr(v).map((x) => x.toLowerCase());
}

function productHasAny(productValues: string[], wanted: string[]): boolean {
  if (wanted.length === 0) return true;
  const set = new Set(productValues.map((x) => x.toLowerCase()));
  return wanted.some((w) => set.has(w.toLowerCase()));
}

/** Fragrance family is derived from the sensory profile scent + fragrance tags. */
export function fragranceFamilyOf(p: SearchableProduct): string[] {
  const families: string[] = [];
  const sensory = p.sensory_profile as Record<string, unknown> | null;
  const scent = sensory && typeof sensory === "object" ? (sensory.scent as string | undefined) : undefined;
  if (scent) families.push(scent);
  for (const t of arr(p.tags)) {
    if (/floral|citrus|woody|herbal|fresh|earthy|spicy|tropical|unscented|sweet/i.test(t)) families.push(t);
  }
  return families;
}

/** Apply all active filters (AND across facet groups, OR within a group). */
export function applyFilters(catalog: SearchableProduct[], f: CommerceFilters): SearchableProduct[] {
  return catalog.filter((p) => {
    if (f.brandSlugs.length && !(p.brand_slug && f.brandSlugs.includes(p.brand_slug))) return false;
    if (f.productTypes.length && !f.productTypes.includes(p.product_type)) return false;
    if (!productHasAny(arr(p.wellness_goals), f.wellnessGoals)) return false;
    if (!productHasAny(arr(p.concerns), f.concerns)) return false;
    if (!productHasAny(arr(p.ingredient_tags), f.ingredients)) return false;
    if (!productHasAny(fragranceFamilyOf(p), f.fragranceFamilies)) return false;
    if (!productHasAny(arr(p.seasonality), f.seasonality)) return false;

    const price = p.sale_price != null && Number(p.sale_price) > 0 ? Number(p.sale_price) : Number(p.price ?? 0);
    if (f.priceMin != null && price < f.priceMin) return false;
    if (f.priceMax != null && price > f.priceMax) return false;

    if (f.vegan) {
      const tags = lowerArr(p.tags);
      if (!VEGAN_TOKENS.some((t) => tags.includes(t))) return false;
    }
    if (f.crueltyFree) {
      const tags = lowerArr(p.tags);
      if (!CRUELTY_TOKENS.some((t) => tags.includes(t))) return false;
    }
    if (f.bundle && !p.is_bundle) return false;
    if (f.giftSet && p.bundle_kind !== "gift_set") return false;
    if (f.seasonal && !p.seasonal_collection && arr(p.seasonality).length === 0) return false;
    if (f.inStock && !p.in_stock) return false;
    return true;
  });
}

// ---------- Sorting ----------

export type SortKey = "relevance" | "newest" | "price_asc" | "price_desc" | "best_selling" | "featured";

export function sortProducts(list: SearchableProduct[], sort: SortKey): SearchableProduct[] {
  const priceOf = (p: SearchableProduct) =>
    p.sale_price != null && Number(p.sale_price) > 0 ? Number(p.sale_price) : Number(p.price ?? 0);
  const copy = [...list];
  switch (sort) {
    case "newest":
      return copy.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    case "price_asc":
      return copy.sort((a, b) => priceOf(a) - priceOf(b));
    case "price_desc":
      return copy.sort((a, b) => priceOf(b) - priceOf(a));
    case "best_selling":
      return copy.sort((a, b) => (b.sales_count ?? 0) - (a.sales_count ?? 0));
    case "featured":
      return copy.sort(
        (a, b) =>
          Number(b.is_featured) - Number(a.is_featured) ||
          (a.merch_pin_order ?? 9999) - (b.merch_pin_order ?? 9999) ||
          (b.sales_count ?? 0) - (a.sales_count ?? 0)
      );
    default:
      return copy;
  }
}

/** Compute the set of facet values actually present in a catalog (for filter UI). */
export function availableFacets(catalog: SearchableProduct[]) {
  const collect = (sel: (p: SearchableProduct) => string[]) => {
    const counts = new Map<string, number>();
    for (const p of catalog) for (const v of sel(p)) counts.set(v, (counts.get(v) ?? 0) + 1);
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, count }));
  };
  return {
    wellnessGoals: collect((p) => arr(p.wellness_goals)),
    concerns: collect((p) => arr(p.concerns)),
    ingredients: collect((p) => arr(p.ingredient_tags)),
    fragranceFamilies: collect((p) => fragranceFamilyOf(p)),
    seasonality: collect((p) => arr(p.seasonality)),
    productTypes: collect((p) => [p.product_type]),
  };
}
